import React, { useState } from 'react';
import { Upload, File, CheckCircle, Eye, AlertCircle, Lock, MessageSquare, Send, CheckCircle2 } from 'lucide-react';
import DashboardMessaging from './DashboardMessaging';

interface AdmissionsManagerProps {
    documents: any[];
    onUpdate: () => void;
    onBack?: () => void;
}

const AdmissionsManager: React.FC<AdmissionsManagerProps> = ({ documents, onUpdate, onBack }) => {
    const [uploading, setUploading] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const docTypes = [
        { id: 'passport', label: 'Valid Passport', required: true },
        { id: 'transcripts', label: 'Academic Transcripts', required: true },
        { id: 'degree', label: 'Degree Certificate', required: true },
        { id: 'cv', label: 'CV / Resume (PG only)', required: false },
        { id: 'sop', label: 'SOP / Personal Statement', required: true },
        { id: 'lors', label: 'LORs (if required)', required: false },
        { id: 'english', label: 'English Test Results', required: false },
        { id: 'other', label: 'Supporting Documents', required: false },
    ];

    const getDocStatus = (type: string) => {
        const doc = documents.find(d => d.Type === type);
        return doc ? doc.Status : 'missing';
    };

    // Check if everything is submitted (Locked state)
    // Only lock if at least one is submitted/approved AND none are rejected
    const hasRejected = documents.some(d => d.Status === 'rejected');
    const isLocked = documents.some(d => d.Status === 'submitted' || d.Status === 'approved') && !hasRejected;
    const isReadyToSubmit = docTypes.filter(d => d.required).every(d => getDocStatus(d.id) !== 'missing') && !hasRejected;

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const currentDoc = documents.find(d => d.Type === type);
        const canUpload = !isLocked || (currentDoc && currentDoc.Status === 'rejected');
        if (!canUpload) return;

        const file = event.target.files[0];
        setUploading(type);

        const formData = new FormData();
        formData.append(type, file);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/student/upload', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) throw new Error('Upload failed');
            onUpdate();
        } catch (error) {
            console.error(error);
        } finally {
            setUploading(null);
        }
    };

    const handleFinalSubmit = async () => {
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/student/admissions/submit', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                onUpdate();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleView = async (docId: number) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/student/document/view/${docId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className="max-w-5xl mx-auto animate-fadeIn space-y-8 pb-20">
            <button
                onClick={onBack}
                className="flex items-center gap-2 text-xs font-bold text-[#009B8F] uppercase tracking-widest hover:translate-x-[-4px] transition-all"
            >
                ← Return to Admissions Hub
            </button>

            {/* Header Banner */}
            <div className="bg-[#011E26] rounded-2xl p-8 relative overflow-hidden shadow-lg">
                <div className="relative z-10 flex justify-between items-start">
                    <div>
                        <div className="inline-flex items-center gap-2 bg-[#009B8F]/20 px-3 py-1 rounded border border-[#009B8F]/30 mb-4">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#009B8F] animate-pulse"></div>
                            <span className="text-[10px] font-bold tracking-widest text-[#009B8F] uppercase">Document Vault</span>
                        </div>
                        <h2 className="text-3xl font-serif font-bold text-white mb-2">Upload Your Documents</h2>
                        <p className="text-slate-400 text-sm">Secure submission portal — all documents are encrypted and reviewed by our team.</p>
                    </div>
                    {isLocked && (
                        <div className="flex items-center gap-3 bg-[#009B8F]/20 text-[#009B8F] px-5 py-3 border border-[#009B8F]/30 rounded-xl">
                            <Lock size={16} />
                            <span className="text-[10px] font-bold uppercase tracking-widest">Submitted</span>
                        </div>
                    )}
                </div>
                <div className="absolute top-0 right-0 w-48 h-48 bg-[#009B8F]/10 rounded-full -mr-12 -mt-12 pointer-events-none"></div>
            </div>

            {/* Document Grid */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {docTypes.map((doc) => {
                        const status = getDocStatus(doc.id);
                        const uploadedDoc = documents.find(d => d.Type === doc.id);

                        return (
                            <div key={doc.id} className="group border border-gray-200 rounded-xl p-5 hover:border-[#009B8F]/40 hover:shadow-sm transition-all">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-start gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${status === 'missing' ? 'bg-gray-100 text-gray-400' : 'bg-[#009B8F]/10 text-[#009B8F]'}`}>
                                            <File size={22} />
                                        </div>
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                <h4 className="text-sm font-bold text-slate-800">{doc.label}</h4>
                                                {doc.required && <span className="text-[8px] bg-red-50 text-red-600 px-2 py-0.5 rounded font-bold uppercase tracking-wide border border-red-100">Required</span>}
                                            </div>
                                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                                {status === 'missing' ? 'Not uploaded' : `Uploaded: ${new Date(uploadedDoc.UpdatedAt).toLocaleDateString()}`}
                                            </p>
                                        </div>
                                    </div>

                                    {status !== 'missing' && (
                                        <button
                                            onClick={() => handleView(uploadedDoc.ID)}
                                            className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-[#009B8F] transition-all bg-gray-50 border border-gray-200 rounded-lg hover:border-[#009B8F]/30"
                                        >
                                            <Eye size={16} />
                                        </button>
                                    )}
                                </div>

                                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                    <span className={`text-[9px] font-bold px-3 py-1.5 rounded-lg uppercase tracking-widest border ${
                                        status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                        status === 'submitted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                        status === 'missing' ? 'bg-gray-50 text-gray-400 border-gray-200' :
                                        'bg-amber-50 text-amber-700 border-amber-200'
                                    }`}>
                                        {status}
                                    </span>

                                    <div className="relative">
                                        {status === 'missing' || (status === 'rejected' && !isLocked) ? (
                                            <>
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    id={`file-${doc.id}`}
                                                    onChange={(e) => handleFileChange(e, doc.id)}
                                                />
                                                <label
                                                    htmlFor={`file-${doc.id}`}
                                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all font-bold text-xs uppercase tracking-widest ${
                                                        status === 'rejected'
                                                        ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
                                                        : 'bg-[#009B8F] text-white hover:bg-[#007b72] shadow-sm'
                                                    }`}
                                                >
                                                    <Upload size={13} /> {uploading === doc.id ? 'Uploading...' : status === 'rejected' ? 'Replace' : 'Upload'}
                                                </label>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Submit Section */}
                <div className="mt-8 pt-8 border-t border-gray-100">
                    {isLocked ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="flex items-center gap-5">
                                    <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <CheckCircle2 size={32} className="text-emerald-600" />
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-serif font-bold text-emerald-800 mb-1">Submission Complete</h4>
                                        <p className="text-sm text-emerald-600">Our admissions specialists are reviewing your documents.</p>
                                    </div>
                                </div>
                                <div className="bg-white border border-emerald-200 px-6 py-3 rounded-xl text-center shadow-sm">
                                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-1">Status</p>
                                    <p className="text-[#009B8F] font-bold text-sm uppercase tracking-wider">Expert Review</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="flex items-center gap-5">
                                <div className={`w-16 h-16 rounded-xl flex items-center justify-center font-bold text-xl font-serif transition-all ${isReadyToSubmit ? 'bg-[#009B8F] text-white shadow-lg shadow-[#009B8F]/20' : 'bg-gray-100 text-gray-400'}`}>
                                    {Math.round((docTypes.filter(d => getDocStatus(d.id) !== 'missing').length / docTypes.length) * 100)}%
                                </div>
                                <div>
                                    <h4 className="text-lg font-serif font-bold text-slate-800 mb-1">Upload Progress</h4>
                                    <p className="text-xs text-slate-500 uppercase tracking-widest">All required documents must be uploaded</p>
                                </div>
                            </div>
                            <button
                                disabled={!isReadyToSubmit || submitting}
                                onClick={handleFinalSubmit}
                                className={`px-10 py-4 rounded-xl font-bold text-sm uppercase tracking-widest transition-all ${
                                    isReadyToSubmit
                                        ? 'bg-[#011E26] text-white hover:bg-[#009B8F] shadow-sm'
                                        : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                }`}
                            >
                                {submitting ? 'Submitting...' : 'Submit Portfolio'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdmissionsManager;
